import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const CONTACT_URL = 'https://bixtq589f8.execute-api.us-east-1.amazonaws.com/prod/contact';

const contactItems = [
  { icon: 'email', label: 'support@reanoai.com', href: 'mailto:support@reanoai.com' },
  { icon: 'phone', label: '+91-9138187858',       href: 'tel:+919138187858' },
  { icon: 'phone', label: '+91-8708041134',       href: 'tel:+918708041134' },
];

function EmailIcon() {
  return (
    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function Field({ label, error, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 12.5, fontWeight: 600, color: '#94a3b8', letterSpacing: '0.04em' }}>{label}</label>
      {children}
      {error && <span style={{ fontSize: 12, color: '#f87171' }}>{error}</span>}
    </div>
  );
}

const inputStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 10,
  padding: '11px 14px',
  fontSize: 14,
  color: '#f1f5f9',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  transition: 'border-color 0.2s',
};

function validate(fields) {
  const errors = {};
  if (!fields.name.trim())    errors.name    = 'Name is required';
  if (!fields.email.trim())   errors.email   = 'Email is required';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) errors.email = 'Enter a valid email';
  if (!fields.phone.trim())   errors.phone   = 'Phone is required';
  if (!fields.message.trim()) errors.message = 'Message is required';
  return errors;
}

function ContactForm() {
  const [fields, setFields] = useState({ name: '', email: '', phone: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [focused, setFocused] = useState(null);

  function set(key) {
    return e => {
      setFields(f => ({ ...f, [key]: e.target.value }));
      if (errors[key]) setErrors(er => ({ ...er, [key]: undefined }));
    };
  }

  async function onSubmit(e) {
    e.preventDefault();
    const errs = validate(fields);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    setStatus(null);
    try {
      const res = await fetch(CONTACT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: fields.name.trim(),
          email: fields.email.trim(),
          phone: fields.phone.trim(),
          message: fields.message.trim(),
          source: 'pulse-contact-form',
        }),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setStatus('success');
      setFields({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  }

  const borderColor = key => errors[key] ? 'rgba(248,113,113,0.6)' : focused === key ? 'rgba(71,158,195,0.6)' : 'rgba(255,255,255,0.1)';

  if (status === 'success') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16, padding: '40px 0' }}>
        <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(34,211,238,0.15)', border: '1px solid rgba(34,211,238,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="24" height="24" fill="none" stroke="#22D3EE" strokeWidth="2.2" viewBox="0 0 24 24">
            <polyline points="20 6 9 17 4 12" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <p style={{ fontSize: 16, fontWeight: 700, color: '#f1f5f9', textAlign: 'center' }}>Message sent!</p>
        <p style={{ fontSize: 14, color: '#64748b', textAlign: 'center', lineHeight: 1.6 }}>We'll get back to you within one business day.</p>
        <button onClick={() => setStatus(null)} style={{ marginTop: 8, fontSize: 13, color: '#479EC3', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
          Send another
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="pulse-contact-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label="Full Name *" error={errors.name}>
          <input
            value={fields.name} onChange={set('name')}
            onFocus={() => setFocused('name')} onBlur={() => setFocused(null)}
            placeholder="Your name"
            style={{ ...inputStyle, borderColor: borderColor('name') }}
          />
        </Field>
        <Field label="Phone *" error={errors.phone}>
          <input
            type="tel" value={fields.phone} onChange={set('phone')}
            onFocus={() => setFocused('phone')} onBlur={() => setFocused(null)}
            placeholder="+91-9876543210"
            style={{ ...inputStyle, borderColor: borderColor('phone') }}
          />
        </Field>
      </div>

      <Field label="Email *" error={errors.email}>
        <input
          type="email" value={fields.email} onChange={set('email')}
          onFocus={() => setFocused('email')} onBlur={() => setFocused(null)}
          placeholder="you@company.com"
          style={{ ...inputStyle, borderColor: borderColor('email') }}
        />
      </Field>

      <Field label="Message *" error={errors.message}>
        <textarea
          value={fields.message} onChange={set('message')}
          onFocus={() => setFocused('message')} onBlur={() => setFocused(null)}
          placeholder="Tell us about your AI goals or challenge…"
          rows={4}
          style={{ ...inputStyle, borderColor: borderColor('message'), resize: 'vertical', minHeight: 100 }}
        />
      </Field>

      {status === 'error' && (
        <div style={{ padding: '10px 14px', borderRadius: 9, fontSize: 13, background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.25)', color: '#f87171' }}>
          Something went wrong. Please try again.
        </div>
      )}

      <button type="submit" disabled={submitting} style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        padding: '13px 0', borderRadius: 10, border: 'none', cursor: submitting ? 'not-allowed' : 'pointer',
        background: 'linear-gradient(135deg,#479EC3,#22D3EE)', color: '#fff',
        fontSize: 15, fontWeight: 700, opacity: submitting ? 0.7 : 1,
        transition: 'opacity 0.2s',
      }}>
        {submitting ? 'Sending…' : <>Send Message <ArrowRight size={16} /></>}
      </button>
    </form>
  );
}

export default function FinalCTA() {
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <section id="contact" style={{ padding: '100px 24px 100px', position: 'relative', overflow: 'hidden' }}>
      {/* Animated glow */}
      <motion.div
        animate={{ opacity: [0.15, 0.3, 0.15] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        style={{ position: 'absolute', top: '40%', left: '50%', transform: 'translate(-50%,-50%)', width: 700, height: 400, pointerEvents: 'none', willChange: 'opacity' }}>
        <div style={{ width: '100%', height: '100%', background: 'radial-gradient(ellipse,#479EC3 0%,#22D3EE 40%,transparent 70%)', filter: 'blur(80px)' }} />
      </motion.div>
      <motion.div
        animate={{ opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 4, repeat: Infinity, delay: 1.5, ease: 'easeInOut' }}
        style={{ position: 'absolute', top: '30%', left: '20%', width: 300, height: 300, pointerEvents: 'none', willChange: 'opacity' }}>
        <div style={{ width: '100%', height: '100%', background: '#6366f1', borderRadius: '50%', filter: 'blur(120px)' }} />
      </motion.div>

      {/* Grid overlay */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.06,
        backgroundImage: 'linear-gradient(to right,#22D3EE 1px,transparent 1px),linear-gradient(to bottom,#22D3EE 1px,transparent 1px)',
        backgroundSize: '64px 64px', pointerEvents: 'none',
      }} />

      {/* Hero copy */}
      <div style={{ maxWidth: 780, margin: '0 auto', position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <h2 style={{ fontSize: 'clamp(32px, 5vw, 60px)', fontWeight: 800, lineHeight: 1.1, marginBottom: 20, letterSpacing: '-0.02em' }}>
            Your AI support team is{' '}
            <span style={{ background: 'linear-gradient(135deg,#479EC3,#22D3EE)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              one upload away.
            </span>
          </h2>

          <p style={{ fontSize: 'clamp(16px, 2vw, 20px)', color: '#94a3b8', maxWidth: 560, margin: '0 auto 48px', lineHeight: 1.6 }}>
            Train, customize, and deploy AI agents that truly understand your business. No code. No waiting. Just results.
          </p>

          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.97 }}
            onClick={() => navigate(user ? '/dashboard' : '/auth')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              padding: '16px 36px', borderRadius: 12, border: 'none', cursor: 'pointer',
              background: 'linear-gradient(135deg,#479EC3,#22D3EE)', color: '#fff',
              fontSize: 17, fontWeight: 700,
              boxShadow: '0 0 40px rgba(34,211,238,0.35)',
            }}>
            Start Free <ArrowRight size={20} />
          </motion.button>

          <p style={{ marginTop: 24, fontSize: 13, color: '#475569' }}>No credit card required · Cancel anytime</p>
        </motion.div>
      </div>

      {/* Contact card */}
      <motion.div
        id="contact-form"
        initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.6, delay: 0.1 }}
        style={{ maxWidth: 960, margin: '72px auto 0', position: 'relative', zIndex: 1,
          background: 'rgba(15,23,42,0.7)', backdropFilter: 'blur(20px)',
          border: '1px solid rgba(71,158,195,0.2)', borderRadius: 24,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)',
          overflow: 'hidden',
        }}>
        {/* Gradient accent */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'radial-gradient(circle at top right, rgba(71,158,195,0.12), transparent 55%)', pointerEvents: 'none' }} />

        <div className="pulse-contact-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', position: 'relative', zIndex: 1 }}>
          {/* Left — info */}
          <div style={{ padding: '44px 40px', borderRight: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', gap: 32 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#479EC3', marginBottom: 12 }}>Get in touch</div>
              <h3 style={{ fontSize: 22, fontWeight: 800, color: '#f1f5f9', lineHeight: 1.3, marginBottom: 10, letterSpacing: '-0.02em' }}>
                Let's talk about your AI goals
              </h3>
              <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.7 }}>
                Have questions or want a personalised walkthrough? Drop us a message and we'll get back within one business day.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {contactItems.map(({ icon, label, href }) => (
                <a key={href} href={href} style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', color: '#94a3b8',
                  fontSize: 14, fontWeight: 500, transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                  onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}>
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(71,158,195,0.1)', border: '1px solid rgba(71,158,195,0.2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#479EC3' }}>
                    {icon === 'email' ? <EmailIcon /> : <PhoneIcon />}
                  </div>
                  {label}
                </a>
              ))}
            </div>
          </div>

          {/* Right — form */}
          <div style={{ padding: '44px 40px' }}>
            <ContactForm />
          </div>
        </div>
      </motion.div>

      <style>{`
        @media (max-width: 700px) {
          .pulse-contact-grid { grid-template-columns: 1fr !important; }
          .pulse-contact-grid > div:first-child { border-right: none !important; border-bottom: 1px solid rgba(255,255,255,0.06); }
          .pulse-contact-row { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
